import MaterialBuilder from "../rendering/material-builder";
import RenderManager from "../rendering/render-manager";
import { fire } from "../utils/events";

let VOLUME_RENDER_ORDER = 3;
let ELECTRIC_FIELD_RENDER_ORDER = 1;
let MAGNETIC_FIELD_RENDER_ORDER = 2;
let FIELD_OPACITY = 0.5;

let LINE_WIDTH = 0.01;

let ELECTRIC_COLOR = 0xff0000;
let MAGNETIC_COLOR = 0x0000ff;

let ARROW_SIZE = new THREE.Vector3(0.3, 0.6, 0.3);

class CoilEmField extends THREE.Object3D {
    constructor({ paramCallback }) {
        super();

        this.isCoilEmField = true;
        this.type = "CoilEmField";

        this._params = {
            coilSize: {
                radius: 0.1,
                length: 1,
            },
            turns: 40,
            current: 15,
            voltage: 30,
            controls: true,
        };

        let coilMaterial = MaterialBuilder.build({ color: 0xaaaaaa, materialType: "pbr", transparent: true, opacity: 0.2 });
        let coilVolumeGeometry = new THREE.CylinderBufferGeometry(1, 1, 1, 32);
        this._coilVolume = new THREE.Mesh(coilVolumeGeometry, coilMaterial);
        this._coilVolume.renderOrder = VOLUME_RENDER_ORDER;

        this._arrowGeo = new THREE.ConeBufferGeometry(0.5, 1, 16, 1);
        this._elArrowMat = MaterialBuilder.build({ color: ELECTRIC_COLOR, transparent: FIELD_OPACITY != 1, opacity: FIELD_OPACITY, materialType: "pbr" });
        this._magArrowMat = MaterialBuilder.build({ color: MAGNETIC_COLOR, transparent: FIELD_OPACITY != 1, opacity: FIELD_OPACITY, materialType: "pbr" });

        this._elLineMat = MaterialBuilder.build({ color: ELECTRIC_COLOR, transparent: FIELD_OPACITY != 1, opacity: FIELD_OPACITY, materialType: "pbr" });
        this._magLineMat = MaterialBuilder.build({ color: MAGNETIC_COLOR, transparent: FIELD_OPACITY != 1, opacity: FIELD_OPACITY, materialType: "pbr" });
        this._elLines = [];
        this._magLines = [];

        this._fieldLines = new THREE.Group();
        this._fieldArrows = new THREE.Group();
        this.add(this._coilVolume, this._fieldLines, this._fieldArrows);

        this._rebuildElectricFieldLines();
        this._rebuildMagneticFieldLines();

        this._coilVolume.dontShowInHierarchyPanel = true;
        this._fieldLines.dontShowInHierarchyPanel = true;
        this._fieldArrows.dontShowInHierarchyPanel = true;

        this._paramCallback = paramCallback;

        this.updateParams();
        this.update();

        let visible = this.visible;
        Object.defineProperty(this, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                fire('em_field_toggle_visibility', this);
            },
        });
    }

    _rebuildMagneticFieldLines() {
        for (let magLine of this._magLines) {
            for (let magArrow of magLine.arrows) {
                magArrow.arrow.parent.remove(magArrow.arrow);
            }
            this._fieldLines.remove(magLine.line);
        }
        this._magLines.length = 0;

        let params = this._params;
        let coilSize = params.coilSize;

        let arrowScale = ARROW_SIZE.clone().multiplyScalar(this._params.coilSize.radius);
        let lineCount = params.current * params.turns * 0.05;

        let magLineParams = [];

        let lineCountInRadius = Math.sqrt(2 * lineCount / Math.PI)
        let radSegments = Math.ceil(lineCountInRadius);
        for (let r_i = 0; r_i < radSegments; r_i++) {
            let radius = coilSize.radius * (r_i + 0.5) / radSegments;

            let lineCountInCircle = (r_i + 0.5) / radSegments * lineCountInRadius * Math.PI;
            let angSegments = Math.max(2, Math.ceil(lineCountInCircle));
            for (let a_i = 0; a_i < angSegments; a_i++) {
                let angle = a_i / angSegments * Math.PI * 2
                magLineParams.push({ angle: angle, pos: radius });
            }
        }

        this._magLineShapesByPos = {};
        for (let { angle, pos } of magLineParams) {
            if (!(pos in this._magLineShapesByPos)) {
                this._magLineShapesByPos[pos] = new MagneticFieldLineShape(coilSize, pos);
            }
        }

        for (let { angle, pos } of magLineParams) {
            let lineShape = this._magLineShapesByPos[pos];
            let magLine = new THREE.Mesh(lineShape.geometry, this._magLineMat);
            magLine.renderOrder = MAGNETIC_FIELD_RENDER_ORDER;
            magLine.rotation.y = angle;

            let lineEntry = {
                shape: lineShape,
                line: magLine,
                arrows: [],
            };

            this._fieldLines.add(magLine);
            this._magLines.push(lineEntry);

            let magArrow = new THREE.Mesh(this._arrowGeo, this._magArrowMat);
            magArrow.renderOrder = MAGNETIC_FIELD_RENDER_ORDER;
            magArrow.rotation.z = -Math.PI * 0.5
            magArrow.scale.copy(arrowScale);
            magLine.add(magArrow);
            lineEntry.arrows.push({
                offset: Math.random(),
                arrow: magArrow,
            });
        }
    }

    _rebuildElectricFieldLines() {
        for (let elLine of this._elLines) {
            for (let elArrow of elLine.arrows) {
                elArrow.arrow.parent.remove(elArrow.arrow);
            }
            this._fieldLines.remove(elLine.line);
        }
        this._elLines.length = 0;

        let arrowScale = ARROW_SIZE.clone().multiplyScalar(this._params.coilSize.radius);

        let lineShape = new ElectricFieldLineShape(this._params.coilSize, this._params.turns);
        let elLine = new THREE.Mesh(lineShape.geometry, this._elLineMat);
        elLine.renderOrder = ELECTRIC_FIELD_RENDER_ORDER;

        let lineEntry = {
            shape: lineShape,
            line: elLine,
            arrows: [],
        };

        this._fieldLines.add(elLine);
        this._elLines.push(lineEntry);

        let arrowCount = Math.ceil(this._params.turns * 1.5 * this._params.voltage * 0.05);
        for (let i = 0; i < arrowCount; i++) {
            let elArrow = new THREE.Mesh(this._arrowGeo, this._elArrowMat);
            elArrow.renderOrder = ELECTRIC_FIELD_RENDER_ORDER;
            elArrow.rotation.z = -Math.PI * 0.5
            elArrow.scale.copy(arrowScale);
            elLine.add(elArrow);
            lineEntry.arrows.push({
                offset: i / arrowCount,
                arrow: elArrow,
            });
        }
    }

    get coilRadius() { return this._params.coilSize.radius; }
    get coilLength() { return this._params.coilSize.length; }
    get turns() { return this._params.turns; }
    get current() { return this._params.current; }
    get voltage() { return this._params.voltage; }

    set coilRadius(r) {
        this._params.coilSize.radius = r;
        this.updateParams();

        if (this._paramCallback) {
            this._paramCallback(this, 'coilRadius', r);
        }
    }

    set coilLength(l) {
        this._params.coilSize.length = l;
        this.updateParams();

        if (this._paramCallback) {
            this._paramCallback(this, 'coilLength', l);
        }
    }

    set turns(t) {
        this._params.turns = t;
        this.updateParams();
        fire('em_field_change_param', this);

        if (this._paramCallback) {
            this._paramCallback(this, 'turns', t);
        }
    }

    set current(c) {
        this._params.current = c;
        this.updateParams();
        fire('em_field_change_param', this);

        if (this._paramCallback) {
            this._paramCallback(this, 'current', c);
        }
    }

    set voltage(v) {
        this._params.voltage = v;
        this.updateParams();
        fire('em_field_change_param', this);

        if (this._paramCallback) {
            this._paramCallback(this, 'voltage', v);
        }
    }

    updateParams() {
        let params = this._params;
        let coilSize = params.coilSize;

        this._coilVolume.scale.set(coilSize.radius, coilSize.length, coilSize.radius);
        this._rebuildMagneticFieldLines();
        this._rebuildElectricFieldLines();
        this.update();
    }

    update() {
        requestAnimationFrame(this.update.bind(this));

        if (!this.visible) return;

        RenderManager.activeCamera.update = true;

        let dir0 = new THREE.Vector3(0, 1, 0);

        let time = Date.now();
        let elSpeed = 1 / 20000, magSpeed = 1 / 3000;

        let current = this._params.current;

        let elOff = time * elSpeed;
        for (let line of this._elLines) {
            let curve = line.shape.curve;
            let invCurveLength = 1 / curve.getLength();

            for (let { offset, arrow } of line.arrows) {
                let arrow_a = current * elOff * invCurveLength + offset;
                arrow_a -= Math.floor(arrow_a);

                let pos = curve.getPointAt(arrow_a), dir = curve.getTangentAt(arrow_a);
                arrow.position.copy(pos);
                arrow.quaternion.setFromUnitVectors(dir0, dir);
            }
        }

        let magOff = time * magSpeed;
        for (let line of this._magLines) {
            let curve = line.shape.curve;
            let invCurveLength = 1 / curve.getLength();

            for (let { offset, arrow } of line.arrows) {
                let arrow_a = magOff * invCurveLength + offset;
                arrow_a -= Math.floor(arrow_a);

                let pos = curve.getPointAt(arrow_a), dir = curve.getTangentAt(arrow_a);
                arrow.position.copy(pos);
                arrow.quaternion.setFromUnitVectors(dir0, dir);
            }
        }
    }

    get controlsEnabled() {
        return this._params.controls;
    }

    set controlsEnabled(enabled) {
        this._params.controls = enabled;
        fire('em_field_toggle_controls', this);

        if (this._paramCallback) {
            this._paramCallback(this, 'controlsEnabled', enabled);
        }
    }
};

function makeGeometryFromCurve(curve, radius) {
    let geometry = new THREE.TubeBufferGeometry(curve, curve.curves.length, radius, 3, false);
    return geometry;
}

class FieldLineShape {
    constructor(curve, radius) {
        this.curve = curve;
        this.geometry = makeGeometryFromCurve(curve, radius);
    }
}

class MagneticFieldLineShape extends FieldLineShape {
    constructor(coilSize, pos) {
        let { radius, length } = coilSize;

        let relLength = length / radius;

        let singleSidePoints = [];

        let startPoint = new THREE.Vector3(pos, 0, 0);
        let point = startPoint.clone().divideScalar(radius);
        let magDir = new THREE.Vector3();
        let coilPos = new THREE.Vector3();
        let offset = new THREE.Vector3();
        let magAdd = new THREE.Vector3();
        let current = new THREE.Vector3();

        for (let i = 0; i < 1000; i++) {
            magDir.set(0, 0, 0);

            for (let j = 0; j < 2; j++) {
                coilPos.x = (j * 2 - 1);
                current.z = j * 2 - 1;

                for (let k = 0; k < 1000; k++) {
                    coilPos.y = (k / 1000 - 0.5) * relLength;
                    offset.subVectors(point, coilPos);

                    magAdd.crossVectors(offset, current);
                    magAdd.multiplyScalar(1 / offset.lengthSq());
                    magDir.add(magAdd);
                }
            }

            magDir.setLength(0.05);
            point.add(magDir);

            let end = point.y < 0;
            if (end) point.y = 0;

            singleSidePoints.push(point.clone().multiplyScalar(radius));

            if (end) break;
        }

        let points = [];
        for (let i = singleSidePoints.length - 1; i >= 0; i--) {
            let point = singleSidePoints[i].clone();
            point.y = -point.y;
            points.push(point);
        }
        points.push(startPoint);
        for (let point of singleSidePoints) points.push(point);

        let curve = new THREE.CurvePath();

        for (let i0 = 0; i0 < points.length - 1; i0++) {
            let i1 = (i0 + 1) % points.length;
            curve.curves[i0] = new THREE.LineCurve3(points[i0], points[i1]);
        }

        super(curve, coilSize.radius * 0.025);
    }
}

class ElectricFieldLineShape extends FieldLineShape {
    constructor(coilSize, turns) {
        let { radius, length } = coilSize;

        let points = [];

        points.push(new THREE.Vector3(-radius * 4, -length * 0.5, radius));
        let segmentCount = 64 * turns - 32;
        for (let i = 0; i <= segmentCount; i++) {
            let angle = Math.PI * 2 * i / 64;
            let pos = (i / segmentCount - 0.5) * length;
            points.push(new THREE.Vector3(Math.sin(angle) * radius, pos, Math.cos(angle) * radius));
        }
        points.push(new THREE.Vector3(-radius * 4, length * 0.5, -radius));

        let curve = new THREE.CurvePath();

        for (let i0 = 0; i0 < points.length - 1; i0++) {
            let i1 = i0 + 1;
            curve.curves[i0] = new THREE.LineCurve3(points[i0], points[i1]);
        }

        super(curve, coilSize.radius * 0.025);
    }
}

export default CoilEmField;
export { CoilEmField };
